/**
 * Auth store using Zustand
 * Manages authentication state with 2FA support
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, getErrorMessage, User, LoginResponse } from '@services/api';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // 2FA state
  requires2FA: boolean;
  tempToken: string | null;

  // Actions
  login: (email: string, password: string) => Promise<LoginResponse>;
  verify2FA: (code: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setUser: (user: User) => void;
  clearError: () => void;
  clear2FA: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      requires2FA: false,
      tempToken: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null, requires2FA: false, tempToken: null });
        try {
          const response = await api.login(email, password);

          if (response.requires_2fa) {
            set({
              requires2FA: true,
              tempToken: response.temp_token || null,
              isLoading: false,
            });
          } else if (response.user) {
            set({
              user: response.user,
              isAuthenticated: true,
              isLoading: false,
            });
          }
          return response;
        } catch (error: any) {
          const message = getErrorMessage(error, 'Login failed');
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      verify2FA: async (code: string) => {
        const { tempToken } = get();
        if (!tempToken) throw new Error('No temp token');

        set({ isLoading: true, error: null });
        try {
          const response = await api.login2FA(code, tempToken);
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            requires2FA: false,
            tempToken: null,
          });
        } catch (error: any) {
          const message = getErrorMessage(error, 'Invalid code');
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      register: async (data: any) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.register(data);
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          set({
            error: getErrorMessage(error, 'Registration failed'),
            isLoading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        await api.logoutBackend();
        set({
          user: null,
          isAuthenticated: false,
          error: null,
          requires2FA: false,
          tempToken: null,
        });
      },

      refreshUser: async () => {
        try {
          const user = await api.getCurrentUser();
          set({ user });
        } catch {
          get().logout();
        }
      },

      setUser: (user: User) => set({ user }),
      clearError: () => set({ error: null }),
      clear2FA: () => set({ requires2FA: false, tempToken: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
